import { ModuleTree } from "vuex";
import { RequestStackItem } from "@/interfaces/http/request-stack-item";
import {
  getIdentifiedNotification,
  IdentifiedNotification,
  Notification
} from "@/models/notification.model";

/**
 * @author Damian Kajzer <damian.kajzer@sowlgroup.com>
 * Vuex app UI module
 */
export const UiModule: ModuleTree<unknown> = {
  ui: {
    namespaced: true,
    state: {
      topNavbarOpened: false,
      topNavbarBannerOpened: false,
      requestStack: [] as RequestStackItem[],
      notifications: [] as IdentifiedNotification[]
    },
    getters: {
      /**
       * Is any request currently pending?
       * @param state Vuex state
       */
      isAnyRequestPending(state): boolean {
        return state.requestStack.length > 0;
      },

      /**
       * Is any aliased request currently pending?
       * @param state Vuex state
       */
      isAnyAliasedRequestPending(state): (p: string) => boolean {
        return alias =>
          state.requestStack.filter(
            (item: RequestStackItem) => item.alias === alias
          ).length > 0;
      }
    },
    actions: {
      /**
       * Triggers new request stack item process - informs that new request was prepared
       * @param commit Vuex commit
       * @param newRequestStackItem New request data
       */
      pushRequestStackItem({ commit }, newRequestStackItem: RequestStackItem) {
        commit("newRequest", newRequestStackItem);
      },

      /**
       * Triggers request item pop from stack.
       * It means that request was finalized - either with success or failure.
       * @param commit Vuex commit
       * @param requestId Request unique id
       */
      popRequestStackItem({ commit }, requestId: string) {
        commit("requestFinalized", requestId);
      },

      /**
       * Triggers new notification
       * @param commit Vuex commit
       * @param notification Notification data
       */
      addNotification({ commit }, notification: Notification) {
        commit("notificationAdded", notification);
      },

      /**
       * Triggers notification close
       * @param commit Vuex commit
       * @param notificationId Notification id
       */
      closeNotification({ commit }, notificationId: string) {
        commit("notificationClosed", notificationId);
      }
    },
    mutations: {
      /**
       * Toggles top navbar opened state
       * @param state Vuex store state
       */
      toggleTopNavbar(state) {
        state.topNavbarOpened = !state.topNavbarOpened;
      },

      /**
       * Toggles top navbar banner opened state
       * @param state
       */
      toggleTopNavbarBanner(state) {
        state.topNavbarBannerOpened = !state.topNavbarBannerOpened;
      },

      /**
       * Adds new item to request stack
       * @param state Vuex state
       * @param newRequestStackItem New request data
       */
      newRequest(state, newRequestStackItem: RequestStackItem) {
        state.requestStack.push(newRequestStackItem);
      },

      /**
       * Removes item from request stack
       * @param state Vuex state
       * @param requestId Request id
       */
      requestFinalized(state, requestId: string) {
        state.requestStack = state.requestStack.filter(
          (item: RequestStackItem) => item.id !== requestId
        );
      },

      /**
       * Adds new notification to notifications stack
       * @param state Vuex state
       * @param notification Notification data
       */
      notificationAdded(state, notification: Notification) {
        state.notifications = [getIdentifiedNotification(notification)].concat(
          state.notifications
        );
      },

      /**
       * Removes notification from notifications stack
       * @param state Vuex state
       * @param notificationId Notification id to remove
       */
      notificationClosed(state, notificationId: string) {
        state.notifications = state.notifications.filter(
          (notification: IdentifiedNotification) =>
            notification.id !== notificationId
        );
      }
    }
  }
};
